import * as THREE from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import { inked, toon } from "../toon";
import { mergeFlat, smooth } from "./geom";
import { cloth } from "./materials";

// The natural build's footwear (#75, the realistic rider): shoes shaped from
// a foot's outline instead of the toy's rounded boxes and balls. Same frame
// and footprint as the toy's (toe toward -x, each sole's bottom where the
// toy's is), so the feet stand on the deck where they did. Each part is
// lofted from the outline: rings of it, pulled in and raised, so a shoe is
// a sole and an upper, not a pile of primitives.
//
// - A vulcanized skate shoe: a white rubber sidewall with a raised toe
//   bumper and a thin foxing stripe, a canvas upper that climbs from a low
//   toe box to a padded collar, a tongue, and criss-crossed laces. 4 draws
//   (the toy's is 7).
// - A western boot: a pointed toe, a thin leather sole with a stacked heel,
//   and a shaft with a scalloped top and pull straps. 2 draws (the toy's 6).
// - A moon boot: a thick ribbed sole, a puffy upper and a quilted cuff up
//   the shin, laced up the front. 3 draws (the toy's 4).

/** Where each kind's sole meets the deck, as the toy's does. */
export const SOLE_BOTTOM = { skate: -0.0475, boots: -0.047, moon: -0.053 };
/** Metres per UV unit on a lofted part: about a toy rounded box's face. */
const UV_SCALE = 0.2;
/** The ankle (the foot group's origin) in shoe space: shoes sit 0.03 toe-ward of it. */
const ANKLE_X = 0.03;

export interface Outline {
  /** Toe to heel. */
  length: number;
  /** Widest, across the ball of the foot. */
  width: number;
  /** How round the toe is: 0.5 is a round toe, higher is more pointed. */
  toe: number;
  /** How much the waist (the arch) narrows, as a fraction of the width. */
  waist: number;
}

/**
 * A foot-shaped outline in the ground plane, as (x, z) points: toe at
 * -length/2, heel at +length/2, round at both ends, widest at the ball and
 * narrower through the arch. Walks the +z side toe to heel, then the -z
 * side back, so every point has a mirror partner.
 */
export function footOutline(o: Outline, steps = 20): THREE.Vector2[] {
  const side: THREE.Vector2[] = [];
  for (let i = 0; i <= steps; i++) {
    const s = (i / steps) * Math.PI;
    const x = (-Math.cos(s) * o.length) / 2;
    const u = x / o.length; // -0.5 toe .. 0.5 heel
    const ball = 1 - o.waist * Math.exp(-(((u - 0.12) / 0.16) ** 2));
    const heel = 1 - 0.14 * Math.max(0, u) * 2;
    const round = Math.sin(s) ** (s < Math.PI / 2 ? o.toe : 0.45);
    side.push(new THREE.Vector2(x, (o.width / 2) * ball * heel * round));
  }
  const out = side.slice(0, -1);
  for (let i = steps; i > 0; i--) out.push(new THREE.Vector2(side[i]!.x, -side[i]!.y));
  return out;
}

/** One ring of a loft: the outline pulled toward its middle (x about `cx`, z toward 0) at a height. */
export interface Ring {
  x: number;
  z: number;
  y: (x: number) => number;
  /** Pushes the outline's `i`th point out by this factor: ribs. */
  out?: (i: number) => number;
}

/**
 * Lofts an outline through rings, bottom up: quads between each pair, and
 * optionally the outline triangulated as a floor under the first ring.
 * Faces point out. An upper's last ring is squeezed to the middle line
 * (z 0), which closes it along a ridge; its two sides' normals there are
 * averaged, so it reads as rounded over the top, not creased.
 */
export function loft(outline: THREE.Vector2[], rings: Ring[], caps: { bottom?: boolean } = {}): THREE.BufferGeometry {
  const n = outline.length;
  const xs = outline.map((p) => p.x);
  const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
  const pos: number[] = [];
  const uv: number[] = [];
  // UVs: the distance round the outline across, height up, one unit per UV_SCALE, so cloth weave and leather grain keep their size.
  const around = [0];
  for (let i = 1; i < n; i++) around.push(around[i - 1]! + outline[i]!.distanceTo(outline[i - 1]!));
  for (const r of rings) {
    outline.forEach((p, i) => {
      const f = r.out ? r.out(i) : 1;
      const y = r.y(p.x);
      pos.push(cx + (p.x - cx) * r.x * f, y, p.y * r.z * f);
      uv.push(around[i]! / UV_SCALE, y / UV_SCALE);
    });
  }
  const index: number[] = [];
  for (let k = 0; k + 1 < rings.length; k++) {
    for (let i = 0; i < n; i++) {
      const a = k * n + i;
      const b = k * n + ((i + 1) % n);
      const c = a + n;
      const d = b + n;
      index.push(a, b, c, b, d, c);
    }
  }
  const tris = THREE.ShapeUtils.triangulateShape(outline, []);
  // `triangulateShape` winds its (x, z) triangles to face down (-y) here, as a floor should.
  if (caps.bottom) for (const [a, b, c] of tris) index.push(a!, b!, c!);
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  geo.setIndex(index);
  geo.computeVertexNormals();
  if (rings[rings.length - 1]!.z === 0) {
    // Each outline point's mirror partner across z is (n - i) mod n (see `footOutline`).
    const normal = geo.getAttribute("normal");
    const top = (rings.length - 1) * n;
    const v = new THREE.Vector3();
    const w = new THREE.Vector3();
    for (let i = 1; i < n - i; i++) {
      const [a, b] = [top + i, top + n - i];
      v.fromBufferAttribute(normal, a).add(w.fromBufferAttribute(normal, b)).normalize();
      normal.setXYZ(a, v.x, v.y, v.z);
      normal.setXYZ(b, v.x, v.y, v.z);
    }
  }
  return geo;
}

/**
 * An upper: from the sole's top (`base`) up the sides and over, a quarter
 * ellipse across, closing along a ridge at `height(x)`.
 */
function upperRings(base: (x: number) => number, height: (x: number) => number, steps = 8, puff = 0): Ring[] {
  const rings: Ring[] = [];
  for (let k = 0; k <= steps; k++) {
    const a = (k / steps) * (Math.PI / 2);
    const sa = Math.sin(a);
    const bulge = 1 + puff * Math.sin(a * 2) ** 2;
    rings.push({
      x: (1 - 0.07 * (1 - Math.cos(a))) * (k === 0 ? 1 : bulge ** 0.5),
      z: k === steps ? 0 : Math.cos(a) * bulge,
      y: (x) => base(x) + (height(x) - base(x)) * sa,
    });
  }
  return rings;
}

/**
 * A sole: a slightly rounded bottom edge, then straight up to `top(x)`;
 * `rib` stands every other outline point that far proud round its middle,
 * so ribs run up the sidewall.
 */
function soleRings(bottom: number, top: (x: number) => number, rib = 0): Ring[] {
  const up = (f: number) => (x: number) => bottom + (top(x) - bottom) * f;
  const ribs = (i: number) => (i % 2 ? 1 + rib : 1);
  return [
    { x: 0.965, z: 0.94, y: () => bottom },
    { x: 0.99, z: 0.985, y: () => bottom + 0.004 },
    { x: 1, z: 1, y: () => bottom + 0.008 },
    ...(rib > 0 ? [{ x: 1, z: 1, y: up(0.35), out: ribs }, { x: 1, z: 1, y: up(0.75), out: ribs }] : []),
    { x: 1, z: 1, y: top },
  ];
}

/** Two lines of laces criss-crossing between eyelet rows at these (x, half-width, y) stations, merged. */
function lacesGeometry(stations: [number, number, number][], thick = 0.0045): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const from = new THREE.Vector3();
  const to = new THREE.Vector3();
  const up = new THREE.Vector3(0, 1, 0);
  const q = new THREE.Quaternion();
  for (let i = 0; i + 1 < stations.length; i++) {
    const [x0, w0, y0] = stations[i]!;
    const [x1, w1, y1] = stations[i + 1]!;
    for (const sign of [1, -1]) {
      from.set(x0, y0, sign * w0);
      to.set(x1, y1, -sign * w1);
      const len = from.distanceTo(to);
      const g = new THREE.CapsuleGeometry(thick, len, 2, 6);
      q.setFromUnitVectors(up, to.clone().sub(from).normalize());
      g.applyQuaternion(q);
      g.translate((x0 + x1) / 2, (y0 + y1) / 2 + (sign > 0 ? 0.0015 : 0), 0);
      parts.push(g);
    }
  }
  return mergeFlat(parts);
}

const RUBBER = "#f1ede2";

/** The vulcanized skate shoe (see the top of the file). */
export function buildNaturalShoe(color: string): THREE.Group {
  const bottom = SOLE_BOTTOM.skate;
  const shoe = new THREE.Group();
  shoe.name = "shoe";
  const outline = footOutline({ length: 0.27, width: 0.118, toe: 0.5, waist: 0.1 });
  // The sidewall: 3 cm of rubber, rising into a toe bumper over the front.
  const soleTop = (x: number) => bottom + 0.028 + 0.014 * smooth((-0.085 - x) / 0.045);
  const sole = inked(loft(outline, soleRings(bottom, soleTop), { bottom: true }), toon(RUBBER, { roughness: 0.75 }), 0.01);
  sole.name = "sole";
  shoe.add(sole);

  // A low toe box climbing over the vamp to the collar round the ankle.
  const height = (x: number) => bottom + 0.058 + 0.057 * smooth((x + 0.1) / 0.15) - 0.008 * smooth((x - 0.1) / 0.035);
  const upperMat = cloth(color, false, { roughness: 0.8 });
  const parts = [loft(outline, upperRings(soleTop, height))];
  const collarY = height(ANKLE_X);
  const collar = new THREE.TorusGeometry(0.043, 0.012, 8, 20);
  collar.rotateX(Math.PI / 2);
  collar.scale(1.2, 1, 1);
  collar.translate(ANKLE_X + 0.008, collarY, 0);
  parts.push(collar);
  // The tongue, up out of the front of the collar and leaning back on the shin.
  const tongue = new RoundedBoxGeometry(0.012, 0.04, 0.052, 2, 0.005);
  tongue.rotateZ(-0.3);
  tongue.translate(ANKLE_X - 0.036, collarY + 0.008, 0);
  parts.push(tongue);
  const upper = inked(mergeFlat(parts), upperMat, 0.012);
  upper.name = "upper";
  shoe.add(upper);

  const foxing = new THREE.Mesh(loft(outline, [
    { x: 1.004, z: 1.03, y: () => bottom + 0.017 },
    { x: 1.004, z: 1.03, y: () => bottom + 0.021 },
  ]), toon("#2a2a33", { roughness: 0.7 }));
  foxing.name = "foxing";
  shoe.add(foxing);

  const stations: [number, number, number][] = [];
  for (let i = 0; i < 5; i++) {
    const x = -0.075 + i * 0.022;
    stations.push([x, 0.02, height(x) + 0.003]);
  }
  const laces = new THREE.Mesh(lacesGeometry(stations), toon("#f4f2ec", { roughness: 0.7 }));
  laces.name = "laces";
  shoe.add(laces);
  return shoe;
}

/** The western boot (see the top of the file). */
export function buildNaturalBoot(color: string): THREE.Group {
  const bottom = SOLE_BOTTOM.boots;
  const boot = new THREE.Group();
  boot.name = "shoe";
  const outline = footOutline({ length: 0.28, width: 0.108, toe: 1.1, waist: 0.14 });
  const soleTop = () => bottom + 0.014;
  const soleMat = toon("#3a2418", { roughness: 0.8 }, "leather");
  // A thin sole with a stacked heel under the back, tapering toward the ground.
  const heel = new THREE.CylinderGeometry(0.036, 0.03, 0.026, 12);
  heel.scale(1, 1, 1.25);
  heel.translate(0.1, bottom - 0.012, 0);
  const sole = inked(mergeFlat([loft(outline, soleRings(bottom, soleTop), { bottom: true }), heel]), soleMat, 0.01);
  sole.name = "sole";
  boot.add(sole);

  const height = (x: number) => bottom + 0.045 + 0.06 * smooth((x + 0.1) / 0.12);
  const leather = toon(color, { roughness: 0.55, side: THREE.DoubleSide }, "leather");
  const parts = [loft(outline, upperRings(soleTop, height))];
  // The shaft: open at the top, which dips front and back into a scallop.
  const SEG = 28;
  const shaft = new THREE.CylinderGeometry(0.064, 0.058, 0.18, SEG, 6, true);
  const pos = shaft.getAttribute("position");
  for (let i = 0; i < pos.count; i++) {
    const y = pos.getY(i);
    if (y < 0.089) continue;
    const phi = Math.atan2(pos.getZ(i), pos.getX(i));
    pos.setY(i, y - 0.022 * Math.cos(phi) ** 2);
  }
  shaft.computeVertexNormals();
  shaft.scale(1.08, 1, 0.94);
  shaft.translate(ANKLE_X + 0.01, 0.085, 0);
  parts.push(shaft);
  // Pull straps inside the top, one each side.
  for (const z of [1, -1]) {
    const strap = new RoundedBoxGeometry(0.022, 0.05, 0.006, 2, 0.002);
    strap.translate(ANKLE_X + 0.01, 0.185, z * 0.054);
    parts.push(strap);
  }
  const upper = inked(mergeFlat(parts), leather, 0.012);
  upper.name = "upper";
  boot.add(upper);
  return boot;
}

/** The moon boot (see the top of the file). */
export function buildNaturalMoonBoot(color: string): THREE.Group {
  const bottom = SOLE_BOTTOM.moon;
  const boot = new THREE.Group();
  boot.name = "shoe";
  // Twice the points round, so the ribs are about 2 cm apart.
  const outline = footOutline({ length: 0.3, width: 0.148, toe: 0.4, waist: 0.06 }, 44);
  const soleTop = () => bottom + 0.045;
  const sole = inked(loft(outline, soleRings(bottom, soleTop, 0.035), { bottom: true }), toon("#5b6270", { roughness: 0.85 }), 0.01);
  sole.name = "sole";
  boot.add(sole);

  const height = (x: number) => bottom + 0.12 + 0.03 * smooth((x + 0.08) / 0.1);
  const padded = cloth(color, false, { roughness: 0.8, side: THREE.DoubleSide });
  const parts = [loft(outline, upperRings(soleTop, height, 7, 0.06))];
  // The cuff: a quilted tube up the shin, puffed between its seams.
  const pts: THREE.Vector2[] = [];
  const BANDS = 3;
  const H = 0.14;
  for (let i = 0; i <= 18; i++) {
    const t = i / 18;
    pts.push(new THREE.Vector2(0.088 + 0.008 * Math.sin(t * Math.PI * BANDS) ** 2, t * H));
  }
  const cuff = new THREE.LatheGeometry(pts, 24);
  cuff.translate(ANKLE_X + 0.012, height(ANKLE_X) - 0.05, 0);
  parts.push(cuff);
  const upper = inked(mergeFlat(parts), padded, 0.012);
  upper.name = "upper";
  boot.add(upper);

  // Laced over the vamp and up the front of the cuff.
  const stations: [number, number, number][] = [];
  for (let i = 0; i < 3; i++) {
    const x = -0.075 + i * 0.03;
    stations.push([x, 0.026, height(x) + 0.004]);
  }
  const cuffFront = ANKLE_X + 0.012 - 0.094;
  for (let i = 1; i <= 3; i++) stations.push([cuffFront, 0.03, height(ANKLE_X) - 0.02 + i * 0.035]);
  const laces = new THREE.Mesh(lacesGeometry(stations, 0.005), toon("#d8dde6", { roughness: 0.7 }));
  laces.name = "laces";
  boot.add(laces);
  return boot;
}
